/**
 * 诊断输入表单组件 - 重新设计版
 * 整合鱼缸选择、工具箱选择、用户问题描述和开始分析按钮
 * 采用卡片式设计，提供更好的视觉层次和交互体验
 */

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Droplets,
  Package,
  MessageSquare,
  Plus,
  Eye,
  Loader2,
  ChevronDown,
  ChevronUp,
  Sparkles,
  Info,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { SprayIcon } from "@/components/icons/SprayIcon";
import { Tank } from "@/services/tankService";
import { toolboxService } from "@/services/toolboxService";
import TankSelectionPopover from "@/components/assessments/TankSelectionPopover";
import ToolboxPreviewPanel from "@/components/assessments/ToolboxPreviewPanel";

export interface ToolboxItem {
  name: string;
  type: string;
}

export interface AssessmentInputFormProps {
  /** 已选择的鱼缸 */
  selectedTank: Tank | null;
  /** 鱼缸选择回调 */
  onTankSelect: (tank: Tank) => void;

  /** 已选择的工具箱项目 */
  selectedToolboxItems: ToolboxItem[];
  /** 工具箱项目变更回调 */
  onToolboxChange: (items: ToolboxItem[]) => void;

  /** 用户问题描述 */
  userQuery: string;
  /** 用户问题描述变更回调 */
  onUserQueryChange: (query: string) => void;

  /** 开始分析回调 */
  onStartAnalysis: () => void;

  /** 是否禁用表单（提交中） */
  disabled?: boolean;

  /** 是否显示开始按钮（需要有上传的图片） */
  showStartButton?: boolean;

  /** 单次评估消耗积分 */
  assessmentCost?: number;
}

/**
 * 诊断输入表单组件
 * 采用卡片式设计，整合所有输入项
 */
export function AssessmentInputForm({
  selectedTank,
  onTankSelect,
  selectedToolboxItems,
  onToolboxChange,
  userQuery,
  onUserQueryChange,
  onStartAnalysis,
  disabled = false,
  showStartButton = true,
  assessmentCost = 100,
}: AssessmentInputFormProps) {
  const { t } = useTranslation("assessments");
  const queryClient = useQueryClient();

  // 本地状态
  const [showTankPopover, setShowTankPopover] = useState(false);
  const [showToolboxPreview, setShowToolboxPreview] = useState(false);
  const [showAddToolboxModal, setShowAddToolboxModal] = useState(false);
  const [isExpanded, setIsExpanded] = useState(true);

  // 获取用户工具箱数据
  const { data: userToolbox, isLoading: isLoadingToolbox } = useQuery({
    queryKey: ["user-toolbox-for-assessment"],
    queryFn: () => toolboxService.getToolboxItems(),
    staleTime: 5 * 60 * 1000,
  });

  // 转换工具箱数据为物品列表
  const allToolboxItems = React.useMemo(() => {
    if (!userToolbox) return [];
    const items: ToolboxItem[] = [];
    Object.entries(userToolbox).forEach(([type, typeItems]) => {
      if (Array.isArray(typeItems)) {
        typeItems.forEach((item) => {
          items.push({ name: item.name, type: item.item_type || type });
        });
      }
    });
    return items;
  }, [userToolbox]);

  const toolboxItemCount = allToolboxItems.length;
  const isToolboxChecked = selectedToolboxItems.length > 0;

  // 切换工具箱勾选状态
  const handleToggleToolbox = (checked: boolean) => {
    if (checked && toolboxItemCount > 0) {
      onToolboxChange(allToolboxItems);
    } else {
      onToolboxChange([]);
    }
  };

  // 关闭添加工具箱 Modal 并刷新数据
  const handleCloseAddToolboxModal = () => {
    setShowAddToolboxModal(false);
    queryClient.invalidateQueries({
      queryKey: ["user-toolbox-for-assessment"],
    });
  };

  return (
    <motion.div
      key="input-stage"
      initial={{ opacity: 0, y: 20, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{
        opacity: 0,
        y: -10,
        scale: 0.95,
        transition: { duration: 0.3, ease: "easeOut" },
      }}
      transition={{
        duration: 0.5,
        ease: [0.4, 0, 0.2, 1],
      }}
    >
      <Card className="border-border/60 bg-card/50 backdrop-blur-sm shadow-sm">
        <CardContent className="p-4 space-y-3">
          {/* 标题栏 - 可折叠 */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-primary" />
              <h3 className="text-sm font-semibold text-foreground">
                {t("pipeline.inputForm.title", "诊断信息")}
              </h3>
              <Badge variant="secondary" className="text-xs">
                {t("pipeline.inputForm.optional", "可选")}
              </Badge>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>

          {/* 表单内容 - 可折叠 */}
          <AnimatePresence initial={false}>
            {isExpanded && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="space-y-3 overflow-hidden"
              >
                {/* 1. 鱼缸选择 */}
                <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-border bg-muted/30">
                  {selectedTank ? (
                    <>
                      <Droplets className="h-4 w-4 text-primary shrink-0" />
                      <div className="flex-1 flex items-center gap-2 min-w-0">
                        <span className="text-sm font-medium text-foreground truncate">
                          {selectedTank.name}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {selectedTank.effective_volume}L ·{" "}
                          {selectedTank.water_type_text}
                        </span>
                      </div>
                      <Tooltip>
                        <TankSelectionPopover
                          open={showTankPopover}
                          onOpenChange={setShowTankPopover}
                          selectedTank={selectedTank}
                          onSelect={(tank) => {
                            onTankSelect(tank);
                            setShowTankPopover(false);
                          }}
                        >
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-muted-foreground hover:text-foreground"
                              disabled={disabled}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                        </TankSelectionPopover>
                        <TooltipContent side="top">
                          {t("tankSelection.changeTank", "更换鱼缸")}
                        </TooltipContent>
                      </Tooltip>
                    </>
                  ) : (
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-2">
                        <Droplets className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          {t("tankSelectorInline.noTank", "未选择鱼缸")}
                        </span>
                      </div>
                      <TankSelectionPopover
                        open={showTankPopover}
                        onOpenChange={setShowTankPopover}
                        selectedTank={selectedTank}
                        onSelect={(tank) => {
                          onTankSelect(tank);
                          setShowTankPopover(false);
                        }}
                        side="right"
                        align="start"
                      >
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs gap-1"
                          disabled={disabled}
                        >
                          <Plus className="h-3 w-3" />
                          {t("tankSelectorInline.selectTank", "选择")}
                        </Button>
                      </TankSelectionPopover>
                    </div>
                  )}
                </div>

                {/* 2. 工具箱选择 */}
                <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-border bg-muted/30">
                  {isLoadingToolbox ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">
                        {t("toolbox.loading", "加载中...")}
                      </span>
                    </div>
                  ) : toolboxItemCount === 0 ? (
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm text-muted-foreground">
                          {t("toolbox.empty", "工具箱为空")}
                        </span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs gap-1"
                        onClick={() => setShowAddToolboxModal(true)}
                      >
                        <Plus className="h-3 w-3" />
                        {t("toolbox.addItems", "添加物品")}
                      </Button>
                    </div>
                  ) : (
                    <>
                      <label
                        htmlFor="use-toolbox"
                        className="flex-1 flex items-center gap-2 cursor-pointer select-none"
                      >
                        <Package className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm font-medium text-foreground">
                          {t("toolbox.useMyToolbox", "使用我的工具箱")}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          ({toolboxItemCount})
                        </span>
                      </label>
                      <Tooltip>
                        <ToolboxPreviewPanel
                          open={showToolboxPreview}
                          onOpenChange={setShowToolboxPreview}
                          toolboxData={userToolbox}
                          isLoading={isLoadingToolbox}
                          isError={false}
                        >
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-muted-foreground hover:text-foreground"
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                        </ToolboxPreviewPanel>
                        <TooltipContent side="top">
                          {t("toolbox.previewButton", "预览")}
                        </TooltipContent>
                      </Tooltip>
                      <Checkbox
                        id="use-toolbox"
                        checked={isToolboxChecked}
                        onCheckedChange={handleToggleToolbox}
                        className="h-5 w-5"
                        disabled={disabled}
                      />
                    </>
                  )}
                </div>

                {/* 3. 用户问题描述 */}
                <div className="flex items-center gap-3 px-3 py-2 rounded-lg border border-border bg-muted/30">
                  <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0" />
                  <Input
                    value={userQuery}
                    onChange={(e) => onUserQueryChange(e.target.value)}
                    placeholder={t(
                      "pipeline.userQuery.placeholder",
                      "描述症状...",
                    )}
                    disabled={disabled}
                    className="flex-1 h-8 text-sm bg-background border-muted focus-visible:ring-1"
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* 开始分析按钮 */}
          {showStartButton && (
            <Button
              onClick={onStartAnalysis}
              disabled={disabled}
              className="w-full h-12 text-base font-medium bg-primary hover:bg-primary/90 flex items-center justify-between px-5 group rounded-xl mt-4"
            >
              <span>{t("pipeline.startAnalysis", "开始分析")}</span>
              <div className="flex items-center h-7 pl-4 text-xs font-semibold bg-black/15 dark:bg-white/15 rounded-full transition-colors group-hover:bg-black/25 dark:group-hover:bg-white/25">
                <SprayIcon className="h-4 w-4 mr-1" />
                <span className="leading-none tabular-nums">
                  - {assessmentCost}
                </span>
              </div>
            </Button>
          )}
        </CardContent>
      </Card>

      {/* 添加工具箱 Modal - 优化设计 */}
      <Dialog open={showAddToolboxModal} onOpenChange={setShowAddToolboxModal}>
        <DialogContent className="sm:max-w-[440px] p-0 overflow-hidden border-none bg-background shadow-2xl">
          {/* 顶部装饰性背景 */}
          <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-muted/30 dark:from-muted/10 to-transparent pointer-events-none" />

          <div className="relative p-6 pt-10">
            <DialogHeader className="items-center text-center">
              {/* 中心大图标 */}
              <div className="mb-4 relative">
                <div className="absolute inset-0 bg-primary/10 dark:bg-primary/5 blur-2xl rounded-full" />
                <div className="relative h-16 w-16 bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-primary/10 rounded-2xl border border-primary/20 dark:border-primary/30 flex items-center justify-center rotate-3 hover:rotate-0 transition-transform duration-300 shadow-lg">
                  <Package className="h-8 w-8 text-primary dark:text-primary/90" />
                </div>
              </div>

              <DialogTitle className="text-2xl font-bold tracking-tight text-foreground">
                {t("toolbox.addTitle", "添加工具箱物品")}
              </DialogTitle>

              <DialogDescription className="text-sm text-muted-foreground max-w-[320px] mx-auto mt-2 leading-relaxed">
                {t(
                  "toolbox.addDescription",
                  "Tunzo AI 将根据您的库存提供更精准的养护建议",
                )}
              </DialogDescription>
            </DialogHeader>

            {/* 提示信息 */}
            <div className="mt-6 p-4 rounded-xl bg-primary/5 dark:bg-primary/10 border border-primary/20 dark:border-primary/30 flex items-start gap-3">
              <Info className="h-4 w-4 text-primary dark:text-primary/90 shrink-0" />
              <p className="text-xs sm:text-xs text-foreground/90 dark:text-foreground/80 leading-snug">
                {t(
                  "toolbox.goToToolboxHint",
                  "请前往工具箱页面管理您的水族用品",
                )}
              </p>
            </div>
          </div>

          <DialogFooter className="p-4 bg-muted/20 dark:bg-muted/10 border-t border-border/50 flex flex-row gap-3">
            <Button
              variant="ghost"
              className="flex-1 text-xs font-medium hover:bg-muted/50 dark:hover:bg-muted/30"
              onClick={handleCloseAddToolboxModal}
            >
              {t("toolbox.later", "稍后再说")}
            </Button>
            <Button
              className="flex-1 text-xs font-bold shadow-md shadow-primary/10 dark:shadow-primary/5 active:scale-95 transition-transform bg-foreground text-background hover:bg-foreground/90 dark:bg-foreground dark:text-background dark:hover:bg-foreground/90"
              onClick={() => {
                window.location.href = "/app/toolbox";
              }}
            >
              {t("toolbox.goToToolbox", "前往工具箱")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}

export default AssessmentInputForm;
